'use client'

import { useEffect, useState, useReducer,  useMemo } from 'react';
// We need the main context to access the account state
// import { useContextState } from './Context';
import { eventsReducer, eventsFilterReducer, EventsFilterState } from './Reducers';

// --- Default State Definitions ---
// These are used for initial state and for resetting on logout.
export const defaultAllEventsState = {
    currentEventId: null,
    pendingEvent: null,
    currentEvent: null,
    events: [],
    myEventsCompleted: [],
    myEventsUpcoming: [],
    myEventsCancelled: [],
    myticketsCompleted: [],
    myticketsCancelled: [],
    myticketsUpcoming: [],
    myticketsRequests: [],
    myticketsApproved: [],
    loved: []
};

export const defaultEventsFilterState: EventsFilterState = {
    sort: 'soonToLater',
    byStock: false,
    byFastDelivery: false,
    byRating: 0,
    searchQuery: "",
    statusQuery: "upcoming",
    ticketStatusQuery: "active",
    ticketOrEventStatusQuery: { "event": "upcoming", "ticket": "active", 'operator': 'OR' },
    ticketAndEventStatusQuery: { "event": "upcoming", "ticket": "active", 'operator': 'AND' },
};

export const useEventsContext:any = (signedIn?: boolean) => {
    // --- Access AccountContext to check login status ---
    // const { accountCTX } = useContextState() || {};
    // const signedIn = accountCTX?.signedIn;

    // --- Initialize state with SERVER-SAFE default values ---
    const [eventPageType, setEventPageType] = useState<string>('');
    const [allEventsState, dispatchAllEventsState] = useReducer(eventsReducer, defaultAllEventsState);
    const [eventsState, eventsDispatch] = useReducer(eventsFilterReducer, defaultEventsFilterState);

    
    // --- HYDRATION: Load state from localStorage only for a logged-in user ---
    useEffect(() => {
        // This effect runs once on the client if the user is determined to be logged in.
        if (signedIn) {
            const storedEventId = window.localStorage.getItem("currentEventId");
            if (storedEventId) {
                dispatchAllEventsState({ type: 'SET_CURRENT_EVENT_ID', payload: storedEventId });
            }
            
            const storedEventPageType = window.localStorage.getItem("eventPageType");
            if (storedEventPageType) {
                try {
                    setEventPageType(JSON.parse(storedEventPageType));
                } catch {}
            }

            const storedFiltersJSON = window.localStorage.getItem('eventsFilterState');
            if (storedFiltersJSON) {
                try {
                    const storedFilters = JSON.parse(storedFiltersJSON);
                    eventsDispatch({ type: 'INITIALIZE_FILTERS', payload: storedFilters });
                } catch (error) {
                    console.error("Failed to parse stored filters:", error);
                }
            }
        }
    }, [signedIn]);

    
    // --- PERSISTENCE: Save state back to localStorage only for a logged-in user ---
    useEffect(() => {
        if (signedIn && allEventsState.currentEventId) {
            window.localStorage.setItem("currentEventId", allEventsState.currentEventId);
        }
    }, [signedIn, allEventsState.currentEventId]);

    useEffect(() => {
        // console.log(eventPageType)
        // if (signedIn && eventPageType !== window.localStorage.getItem("eventPageType")) {
        if ( eventPageType !== window.localStorage.getItem("eventPageType")) {
            window.localStorage.setItem("eventPageType", JSON.stringify(eventPageType));
        }
    }, [signedIn, eventPageType]);

    useEffect(() => {
        if (signedIn) {
            try {
                window.localStorage.setItem('eventsFilterState', JSON.stringify(eventsState));
            } catch (error) {
                console.error("Could not save events filter state:", error);
            }
        }
    }, [signedIn, eventsState]);

    // --- NEW: AUTO-RESET ON LOGOUT ---
    useEffect(() => {
        // When signedIn becomes false, reset all state and clear storage.
        if (signedIn === false) {
            // Dispatch our new, safer action instead of the destructive 'RESET_STATE'.
            dispatchAllEventsState({ type: 'CLEAR_USER_DATA' });

            // We still reset filters and page type
            eventsDispatch({ type: 'RESET_FILTERS' });
            setEventPageType('');

            // Clean up this context's specific localStorage items.
            window.localStorage.removeItem("currentEventId");
            window.localStorage.removeItem("eventPageType");
            // NOTE: We do NOT clear the events filter state, as a guest might still use it.
        }
    }, [signedIn]);


    const contextValue = useMemo(() => ({
        allEventsState, 
        dispatchAllEventsState, 
        eventPageType, 
        setEventPageType, 
        eventsState, 
        eventsDispatch
    }), [
        allEventsState, 
        dispatchAllEventsState, 
        eventPageType, 
        setEventPageType, 
        eventsState, 
        eventsDispatch
    ]);

    return contextValue;
}

export type EventsContextValue = ReturnType<typeof useEventsContext>;